/**
 * O ATESTADO QUE A NECTAR INDICA como prova de capacidade (D11, decisão do
 * Pedro em 2026-10-02).
 *
 * "Essa é uma licitação que o NECTAR em si disse que o atestado em questão
 * serve para entrar como prova de capacidade […] nesses momentos a decisão
 * superior prevalece em relação ao sistema." O casador de palavras não via o
 * #166 no SaaS de São Lourenço da Mata, e a matriz o via só "em parte". A
 * pessoa que conhece o acervo diz qual atestado prova, e ele conta como
 * cobertura: por inteiro nas duas perguntas da comissão, na matriz, e a
 * afinidade máxima, pelo objeto. O score é refeito na hora — salvo o da
 * licitação fora do funil (suspensa, vencida), que fica congelado.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ErroApi, type LicitacaoDetalhe } from "../api/cliente";
import { useSessao } from "../sessao";
import { dia } from "../formato";
import { Secao } from "./Secao";
import { descartadaPeloFunil } from "./Selos";
import css from "./AtestadosDeclarados.module.css";

export function AtestadosDeclarados({ d }: { d: LicitacaoDetalhe }) {
  const { podeOperar } = useSessao();
  const cliente = useQueryClient();
  const [codigo, setCodigo] = useState("");
  const [nota, setNota] = useState("");
  const lista = useQuery({
    queryKey: ["declarados", d.id],
    queryFn: () => api.atestadosDeclarados(d.id),
  });

  // O score, a lista e o cartão mudam juntos: tudo o que mostra o número.
  const refaz = () => {
    void cliente.invalidateQueries({ queryKey: ["declarados", d.id] });
    void cliente.invalidateQueries({ queryKey: ["score", d.id] });
    void cliente.invalidateQueries({ queryKey: ["licitacao", d.id] });
    void cliente.invalidateQueries({ queryKey: ["licitacoes"] });
    void cliente.invalidateQueries({ queryKey: ["facetas"] });
  };
  const declara = useMutation({
    mutationFn: () =>
      api.declararAtestado(d.id, { codigo: codigo.trim(), nota: nota.trim() || null }),
    onSuccess: () => {
      setCodigo("");
      setNota("");
      refaz();
    },
  });
  const retira = useMutation({
    mutationFn: (declaracao: number) => api.retirarAtestado(d.id, declaracao),
    onSuccess: refaz,
  });

  const itens = lista.data ?? [];
  if (!podeOperar && itens.length === 0) return null;
  const congelado = descartadaPeloFunil(d.motivo_aba);
  const erro = declara.error ?? retira.error;

  return (
    <Secao titulo="Atestado indicado pela Nectar" conta={itens.length}>
      <div className={css.tudo}>
        {itens.length > 0 ? (
          <ul className={css.lista}>
            {itens.map((a) => (
              <li key={a.id} className={css.item}>
                <div className={css.linha}>
                  <b>{a.codigo}</b>
                  <span className={css.entidade}>
                    {a.entidade ?? "—"}
                    {a.ano ? ` · ${a.ano}` : ""}
                    {a.uf ? ` · ${a.uf}` : ""}
                  </span>
                  {podeOperar ? (
                    <button
                      type="button"
                      className={css.retirar}
                      disabled={retira.isPending}
                      onClick={() => retira.mutate(a.id)}
                    >
                      Retirar
                    </button>
                  ) : null}
                </div>
                <p className={css.quem}>
                  Indicado por {a.declarado_por} em {dia(a.declarado_em)}
                  {a.nota ? <> — “{a.nota}”</> : null}
                </p>
              </li>
            ))}
          </ul>
        ) : null}

        {podeOperar ? (
          <form
            className={css.form}
            onSubmit={(e) => {
              e.preventDefault();
              if (codigo.trim()) declara.mutate();
            }}
          >
            <div className={css.campos}>
              <input
                className={css.codigo}
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                placeholder="Atestado (ex.: 166)"
                aria-label="Código do atestado"
                inputMode="numeric"
              />
              <input
                className={css.nota}
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                placeholder="Por que ele prova (opcional)"
                aria-label="Por que este atestado prova a capacidade"
              />
              <button type="submit" className={css.indicar}
                      disabled={!codigo.trim() || declara.isPending}>
                {declara.isPending ? "Indicando…" : "Indicar como prova"}
              </button>
            </div>
            <p className={css.ajuda}>
              O atestado indicado conta como cobertura por inteiro do objeto e da
              habilitação, e o score é refeito na hora.
              {congelado
                ? " Esta licitação está fora do funil (suspensa ou vencida): o score dela está congelado e só refaz quando ela reabrir."
                : ""}
            </p>
          </form>
        ) : null}

        {erro ? (
          <p className={css.erro}>
            {erro instanceof ErroApi ? erro.message : "Não deu para salvar a indicação."}
          </p>
        ) : null}
      </div>
    </Secao>
  );
}
